/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Address,
  NullPromise,
  QCQuoteValues,
  QuoteFrameBuyInItem,
  V6QuoteAnnotation
} from '../../../webmodule-common/interop/webmodule-interop.js';
import {
  AttachedBlobAsset,
  BinaryDataWrapper,
  CurrencyCode,
  DealerQuotePriceAdjustmentMethod,
  ExternalProviderDefinition,
  InitialPriceDefinition,
  InputCreateQuoteItem,
  InputQuoteState,
  InputUpdateQuoteItem,
  InputUpdateQuoteItems,
  InputUpdateQuotePrice,
  InputUpdateQuoteSetState,
  InputUpdateQuoteStateChangeReason,
  Quote,
  QuoteFinalStateReason,
  QuoteItem,
  QuoteItemBuyInData,
  QuoteItemPrice,
  QuoteItemPriceBreakdown,
  QuoteItemProviderData,
  QuoteItemRowAction,
  QuoteItemState,
  QuotePresentation,
  QuotePrice,
  QuotePriceCalculation,
  QuoteSetSibling,
  QuoteState,
  ResultFullQuotePrice,
  ResultGetQuote,
  ResultGetQuoteItemsSummary,
  ResultQuoteItem,
  UpdateQuoteItem
} from '../../api/dealer-api-interface-quote.js';
import { BlobApi } from '../../api/blob-api.js';
import {
  canAppearOnSupplierOrder,
  getQuoteNumberSuffix,
  isAnyFreehand,
  isDealerQuotePriceAdjustment,
  isFrame,
  isShipping,
  isSSI,
  mustAppearOnPurchaseOrder
} from './quote-helper-functions.js';
import { clone, compare, compareAnalyze } from '../../../webmodule-common/other/clone.js';
import {
  createQuoteProviderData,
  QuoteProviderData,
  validateAndUpgradeQuoteProviderData
} from './quote-provider-data.js';
import { DevelopmentError, showDevelopmentError } from '../../../webmodule-common/other/development-error.js';
import { EventNotify } from '../../../webmodule-common/other/ui/events.js';
import { fetchQuoteAsset, isSupplierUsingSSI } from './v6/helper-functions.js';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away.js';
import { flagEquals, flagInSet, isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions.js';
import { fromJsonStr, toJsonStr } from '../../../webmodule-common/other/blob-converters.js';
import { getQuoteSupplierDisplayName, quoteSupplierProvider } from './quoteSupplierProvider.js';
import { information } from '../../../webmodule-common/other/ui/modal-option.js';
import { InformationDispatcher } from '../../../webmodule-common/other/ui/information-dispatcher.js';
import { LangQuoteContainer } from '../../../localization/scripts/quotes/quote-container-lang';
import { localDateTimeToServer } from '../../../webmodule-common/other/datetime-converter.js';
import { LockResourceResult } from '../../../webmodule-common/other/resource-lock-info.js';
import { money } from '../../../webmodule-common/other/currency-formatter.js';
import { newGuid } from '../../../webmodule-common/other/api/guid.js';
import { PricingModel, pricingModelType } from '../../../webmodule-common/pricing/pricing-model.js';
import { QuoteApi } from '../../api/quote-api.js';
import { QuoteItemContainer } from './quote-item-container.js';
import { QuoteTechnicalReviewDialog } from './quote-technical-review-confirmation.js';
import { runEventNotify } from '../../../webmodule-common/other/array-helper.js';
import { saveWithIndicator, SaveWorkflowModal } from '../../../webmodule-common/other/save-workflow.js';
import { sharedExecute } from '../../../webmodule-common/other/common/helpers/callbacks.js';
import { SSIProcessor } from './ssi-processor.js';
import { StockLookupView } from '../../api/dealer-api-interface-franchisee.js';
import { v6ConfigSupplierOptions } from '../../../webmodule-common/interop/interfaces/v6-config.js';
import { v6SupportsVersion } from '../../../webmodule-common/interop/interfaces/v6-config.js';
import { v6VersionMap } from '../../../webmodule-common/interop/interfaces/v6-config.js';

export class MockSSIProcessor implements SSIProcessor {
  async processSSI(): Promise<InputUpdateQuoteItems | undefined> {
    return undefined;
  }
}

export interface ValidationDetails {
  code: string;
  message: string;
}

export interface PriceValidation {
  id: string;
  details: ValidationDetails[];
}

export interface StockLookupViewExtra extends StockLookupView {
  lookup: string;
  marginPercentage: number | null;
  calculatedGross: number | null;
  // description: string;
}

export interface ParamCreateQuoteItem {
  id: string | null;
  title: string | null;
  description: string | null;
  quantity: number | null;
  comment: string | null;
  commentToManufacturer: string | null;
  externalProvider: ExternalProviderDefinition | null;
  buyInData: BinaryDataWrapper | null;
  quoteItemContentType: number;
  price: InitialPriceDefinition | null;
  isRestrictedToPowerUser: boolean | null;
  isTaxableItem: boolean | null;
}

let _num = 1;
export class QuoteContainer {
  _internalId: number = _num++;
  quoteId: string;
  quote: Quote | null;
  quotePrice: QuotePrice | null;
  private _quoteProviderData: QuoteProviderData | null;
  get quoteProviderData(): QuoteProviderData | null {
    return this._quoteProviderData;
  }
  set quoteProviderData(value: QuoteProviderData | null) {
    this._quoteProviderData = value;
  }
  quotePresentation: QuotePresentation | null = null;
  siblings: QuoteSetSibling[] | null = null;
  items: QuoteItem[] | null;
  deletedItems: QuoteItem[] | null;
  itemsData: QuoteItemProviderData[] | null;
  itemsBuyInData: QuoteItemBuyInData[] | null;
  itemPrices: QuoteItemPrice[] | null;
  deletedItemsData?: QuoteItemProviderData[] | null;
  deletedItemsBuyInData?: QuoteItemBuyInData[] | null;
  deletedItemPrices?: QuoteItemPrice[] | null;
  isNewQuote: boolean;

  constructor(
    quoteId: string,
    quote: Quote | null,
    quotePrice: QuotePrice | null,
    quotePresentation: QuotePresentation | null,
    siblings: QuoteSetSibling[] | null,
    items: QuoteItem[] | null,
    itemsData: QuoteItemProviderData[] | null,
    itemsBuyInData: QuoteItemBuyInData[] | null,
    itemPrices: QuoteItemPrice[] | null,
    quoteProviderData: QuoteProviderData | null = null,
    isNewQuote = false
  ) {
    this.quote = quote;
    this.quotePrice = quotePrice;
    this.quotePresentation = quotePresentation;
    this.siblings = siblings;
    this.items = items;
    this.itemsData = itemsData;
    this.itemsBuyInData = itemsBuyInData;
    this.itemPrices = itemPrices;
    this.deletedItemsData = null;
    this.deletedItemsBuyInData = null;
    this.deletedItemPrices = null;
    this.quoteId = quoteId;
    this.quoteProviderData = quoteProviderData;
    this.isNewQuote = isNewQuote;
  }

  reset() {
    this.quote = null;
    this.quotePrice = null;
    this.quotePresentation = null;
    this.siblings = null;
    this.items = null;
    this.itemsData = null;
    this.itemsBuyInData = null;
    this.itemPrices = null;
    this.deletedItemsData = null;
    this.deletedItemsBuyInData = null;
    this.deletedItemPrices = null;

    this.quoteProviderData = null;
  }
}

export interface IQuoteValidator {
  valid: (state: QuoteState) => Promise<boolean>;
}

// a manager that is used to find and collect quote item information
// and keep a comparison backup
export class QuoteContainerManager {
  backup: QuoteContainer;
  container: QuoteContainer;
  api: QuoteApi;
  afterSave: EventNotify[] = [];
  blobApi: BlobApi;
  protected quoteUpdateAssets: { [key: string]: AttachedBlobAsset } | null = null;
  numberSeed: string | null = null;
  lockInfo?: LockResourceResult;
  priceValidation: PriceValidation[] | null = null;
  quoteValidator?: IQuoteValidator;
  public forceLockout = false;
  public pricingModel: PricingModel = new PricingModel(pricingModelType.margin, 0, 99);
  protected stateChangeReason: InputUpdateQuoteStateChangeReason | null = null;
  /**
   * create and return a container for quote item data references
   * @param quoteItemId the quote item to return
   * @param useBackup if true, return the backup copy, if false the working copy
   * @returns a quoteItemContainer with the data parts combined
   */
  private itemContainerCache: QuoteItemContainer[] = [];
  private itemContainerCacheBak: QuoteItemContainer[] = [];

  constructor(original: QuoteContainer, quoteApi: QuoteApi, blobApi: BlobApi) {
    this.api = quoteApi;
    this.blobApi = blobApi;
    if (original.quote && original.quote.id !== original.quoteId)
      throw new Error(LangQuoteContainer.invalidArgumentQuoteId()); // Localized
    this.container = original;
    this.backup = this.clone(this.container);
  }

  get isV6(): boolean {
    return this.container.quote?.serviceProvider === quoteSupplierProvider.v6;
  }

  public get quoteState(): QuoteState {
    if (globalThis?.dealerQuoteState !== undefined) return globalThis?.dealerQuoteState;
    return this.quote.state;
  }

  public get quoteCurrency(): CurrencyCode {
    return this.quote.quoteCurrency;
  }

  public set quoteState(value: QuoteState) {
    this.quote.state = value;
  }

  get siblings() {
    return this.container.siblings;
  }
  get hasDeletedItems(): boolean {
    return this.container.deletedItems?.length ? true : false;
  }

  hasBreakdowns(id: string) {
    const price = this.container.itemPrices?.find(x => x.id === id);
    return price?.breakdowns && price?.breakdowns.length > 0;
  }

  get isSSINeedsShippingAddress(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.ssi.trigger.shipping';
      if (!(featureFlag in customOptions)) {
        return false;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return false;
    }
  }

  //default behavour true unless disabled
  get is3dModelEnabled(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.3d.model.enabled';
      if (!(featureFlag in customOptions)) {
        return true;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return true;
    }
  }

  get isPricingMetricLockedBySupplier(): boolean {
    try {
      if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
        return false;
      }

      if (!this.quote?.supplierId) {
        return false;
      }

      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      const customOptions = supplierOptions?.customOptions;

      if (!customOptions || Object.keys(customOptions).length === 0) {
        return false;
      }

      const featureFlag = 'quote.supplier-margin-markup-override.enabled';
      if (!(featureFlag in customOptions)) {
        return false;
      }

      return customOptions[featureFlag] === true || customOptions[featureFlag] === 'true';
    } catch {
      return false;
    }
  }

  public get hasFrames(): boolean {
    const items = this.container.items;
    if (!items) return false;

    for (const item of items) {
      if (isFrame(item)) return true;
    }
    return false;
  }

  /**
   * the quoteId for this managed container
   */
  get quoteId(): string {
    return this.container.quoteId;
  }

  get quoteItemPriceTotal(): number {
    return this.getQuoteItemPriceTotal();
  }

  get taxableQuoteItemPriceTotal(): number {
    return this.getTaxableQuoteItemPriceTotal();
  }

  public get quoteTitle(): string {
    if (this.quote.quoteNumber != 0)
      return LangQuoteContainer.quoteTitleNumber({
        quoteNumber: getQuoteNumberSuffix(this.quote),
        title: this.quote.title
      });
    else return LangQuoteContainer.quoteTitleDraft({ title: this.quote.title });
  }

  /**
   * returns the quote object after needsQuote is called, or throws an error if the quote is unavailable.
   */
  get quote(): Quote {
    if (!this.container.quote) {
      throw new Error(LangQuoteContainer.quoteIsNull()); // Localized
    }
    return this.container.quote;
  }

  get quoteItems(): QuoteItem[] {
    if (!this.container.items) throw new Error(LangQuoteContainer.callNeedsQuoteItemsFirst()); // Localized
    return this.container.items;
  }

  get quoteItemPrices(): QuoteItemPrice[] {
    if (!this.container.itemPrices) throw new Error(LangQuoteContainer.callNeedsQuoteItemsFirst()); // Localized
    return this.container.itemPrices;
  }

  /**
   * returns the quote price object after needsQuote is called, or throws an error if the quote price is unavailable.
   */
  get quotePrice(): QuotePrice {
    if (!this.container.quotePrice) {
      throw new Error(LangQuoteContainer.quotePriceIsNull()); // Localized
    }
    return this.container.quotePrice;
  }

  private _lastSaveSuccessful = true;

  public get lastSaveSuccessful(): boolean {
    return this._lastSaveSuccessful;
  }

  public get isSupplierQuote(): boolean {
    if (!this.container.items) throw new DevelopmentError('call needsQuoteItems first');

    const items = this.container.items.filter(item => canAppearOnSupplierOrder(item)) ?? [];

    // if we end up with free hand items and/or shipping only, we should not create a purchase order
    const shouldCreatePO = items.some(item => mustAppearOnPurchaseOrder(item));
    return shouldCreatePO;
  }

  public get isBuyInDataLocked(): boolean {
    // eslint-disable-next-line no-bitwise
    return !flagInSet(this.quoteState, QuoteState.Draft | QuoteState.Active);
  }

  private _supplierName?: string;

  get supplierName(): string {
    return this._supplierName ?? '';
  }

  get dealerPriceAdjustment(): number {
    const item = this.quoteItems.find(x => isDealerQuotePriceAdjustment(x));
    if (!item) return this.quotePrice.priceAdjustment;
    const itemprice = this.quoteItemPrices.find(x => x.id === item.id);
    return (itemprice?.calculatedGrossSellingPrice ?? 0) + this.quotePrice.priceAdjustment;
  }

  get taxRate() {
    return this.quotePrice.taxPercentage / 100;
  }

  public get allowNonTaxableItems(): boolean {
    return this.container.quote?.allowNonTaxableItems ?? false;
  }

  public static async createQuoteServiceProviderData(
    supplierType: string,
    supplierId: string,
    quoteOwnerId: string
  ): NullPromise<QuoteProviderData> {
    return await createQuoteProviderData(supplierType, supplierId, quoteOwnerId);
  }

  applySupplierMarginMarkupOverride(value: number, valueType: 'margin' | 'markup' = 'margin') {
    this.quotePrice.percentMarginOrMarkup = value;
    this.quotePrice.calculation = valueType === 'margin' ? QuotePriceCalculation.Margin : QuotePriceCalculation.Markup;
  }

  getQuoteItemPriceTotal(itemPrices?: QuoteItemPrice[]) {
    let total = 0;
    //if we dont sum this to 2dp, then the UI wont always add up if someone checking.
    //the server will be doing the real calculations anyway
    (itemPrices ?? this.container.itemPrices)?.forEach(x => (total += money(x.calculatedGrossSellingPrice, 2)));
    return total;
  }
  getQuoteItemDealerPriceAdjustmentTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => (total += money(x.priceAdjustment, 2)));
    return total;
  }
  getTaxableQuoteItemPriceTotal(itemPrices?: QuoteItemPrice[]) {
    let total = 0;
    (itemPrices ?? this.container.itemPrices)?.forEach(x => {
      if (x.isTaxableItem && this.quotePrice.taxPercentage !== 0) total += money(x.calculatedGrossSellingPrice, 2);
    });
    return total;
  }

  getNonTaxableQuoteItemPriceTotal(itemPrices?: QuoteItemPrice[]) {
    let total = 0;
    (itemPrices ?? this.container.itemPrices)?.forEach(x => {
      if (!x.isTaxableItem || this.quotePrice.taxPercentage === 0) total += money(x.calculatedGrossSellingPrice, 2);
    });
    return total;
  }

  public async updatePricingRules() {
    throw new DevelopmentError('updatePricingRules not implemented');
  }

  flushQuote() {
    this.itemContainerCache = [];
    this.itemContainerCacheBak = [];
    this.container.reset();
    this.backup = this.clone(this.container);
  }

  async getInstallationAddress(): Promise<Address | null> {
    return null;
  }

  public ssiProcessor(_itemFilter?: string[] | undefined, _extraItems?: QuoteItemContainer[]): SSIProcessor {
    return new MockSSIProcessor();
  }

  public async isQuoteValid(state: QuoteState): Promise<boolean> {
    if (!this.quoteValidator) return true;

    return await this.quoteValidator.valid(state);
  }

  public addPriceValidation(id: string, code: string, message: string) {
    this.priceValidation ??= [];

    let validation = this.priceValidation.find(x => x.id == id);

    if (!validation) {
      validation = new (class implements PriceValidation {
        details: ValidationDetails[] = [];
        id = id;
      })();

      this.priceValidation.push(validation);
    }

    if (validation.details.findIndex(x => x.code == code) < 0) validation.details.push({ code, message });
  }

  public getPriceValidation(id: string) {
    return this.priceValidation?.find(x => x.id == id);
  }

  isReadonly(): boolean {
    return this.internalIsReadonly() || this.isLockedFromUse();
  }

  isLockedFromUse(): boolean {
    return this.forceLockout || !(this.lockInfo?.isLockOwner ?? true);
  }

  /**
   *
   * @param id the quoteItemId
   * @param returnNextPositionIfNotExists
   */
  public itemPosition(id?: string, returnNextPositionIfNotExists?: boolean): number | undefined {
    if (id === undefined) return undefined;
    //TODO - implement a quote item position tracker, managed at the quote level and use this for rendering
    const index = this.container.items?.findIndex(x => x.id === id) ?? -1;
    return index === -1 && returnNextPositionIfNotExists ? (this.container.items?.length ?? 1) : index + 1;
  }

  /**
   * Simple wrapper around structuredClone
   * @param item an object of any basic type to clone
   * @returns
   */
  clone<ItemType>(item: ItemType): ItemType {
    return clone(item);
  }

  /**
   * Locate a price object by quote item id searching active then deleted partitions.
   * @param id Quote item id
   * @param useBackup When true searches backup partitions instead of working container
   */
  private findItemPrice(id: string, useBackup = false): QuoteItemPrice | undefined {
    const c = useBackup ? this.backup : this.container;
    return c.itemPrices?.find(x => x.id === id) || c.deletedItemPrices?.find(x => x.id === id) || undefined;
  }

  getQuoteItemExtendedListPrice(id: string, breakdown?: QuoteItemPriceBreakdown): number {
    const price = breakdown ?? this.findItemPrice(id);
    if (price == undefined) return 0;

    return price.supplierGrossQuantityCost;
  }

  getQuoteItemExtendedListPriceTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => {
      const item = this.getItemById(x.id);
      if (!item) return; //Deleted Item
      if (!isAnyFreehand(item)) total += money(x.supplierGrossQuantityCost, 2);
    });
    return total;
  }

  getQuoteItemMultiplier(id: string, breakdown?: QuoteItemPriceBreakdown): number {
    const price = breakdown ?? this.findItemPrice(id);
    if (price == undefined) return 0;

    return (
      price.supplierNettQuantityCost /
      (price.supplierGrossQuantityCost + (price.supplierGrossQuantityCost == 0 ? 1 : 0))
    );
  }

  getQuoteItemMultiplierTotal(): number {
    let supplierNettQuantityCostTotal = 0;
    let supplierGrossQuantityCostTotal = 0;

    this.container.itemPrices?.forEach(x => {
      const item = this.getItemById(x.id);
      if (!item) return; //Deleted Item
      if (!isAnyFreehand(item)) {
        supplierNettQuantityCostTotal += money(x.supplierNettQuantityCost, 2);
        supplierGrossQuantityCostTotal += money(x.supplierGrossQuantityCost, 2);
      }
    });
    return (
      supplierNettQuantityCostTotal / (supplierGrossQuantityCostTotal + (supplierGrossQuantityCostTotal == 0 ? 1 : 0))
    );
  }

  getQuoteItemSupplierPriceAdjustment(id: string, breakdown?: QuoteItemPriceBreakdown): number {
    // Always lookup the price object for the item (active or deleted)
    const price = this.findItemPrice(id);
    if (!price) return 0;
    const supplierAdjustment = price.supplierPriceAdjustmentEnabled ? price.supplierPriceAdjustment : 0;
    if (breakdown) {
      // Calculate proportional supplierPriceAdjustment for this breakdown
      const totalGross = price.supplierGrossQuantityCost;
      const breakdownGross = breakdown.supplierGrossQuantityCost ?? 0;
      const proportion = totalGross > 0 ? breakdownGross / totalGross : 0;

      const proportionalAdjustment = supplierAdjustment * proportion;
      return proportionalAdjustment;
    }

    return supplierAdjustment;
  }

  getQuoteItemSupplierPriceAdjustmentTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => {
      const supplierAdjustment = x.supplierPriceAdjustmentEnabled ? x.supplierPriceAdjustment : 0;
      const item = this.getItemById(x.id);
      if (!item) return; //Deleted Item
      if (isAnyFreehand(item)) total += money(supplierAdjustment, 2);
    });
    return total;
  }

  getQuoteItemDealerCost(id: string, breakDown?: QuoteItemPriceBreakdown): number {
    // Always lookup the price object for the item (active or deleted)
    const price = this.findItemPrice(id);
    if (!price) return 0;
    const supplierAdjustment = price.supplierPriceAdjustmentEnabled ? price.supplierPriceAdjustment : 0;
    if (breakDown) {
      // Calculate proportional priceAdjustment for this breakdown
      const totalGross = price.supplierGrossQuantityCost;
      const breakdownGross = breakDown.supplierGrossQuantityCost ?? 0;
      const proportion = totalGross > 0 ? breakdownGross / totalGross : 0;
      const proportionalAdjustment = supplierAdjustment * proportion;
      return breakdownGross + proportionalAdjustment;
    }

    return price.supplierNettQuantityCost + supplierAdjustment;
  }

  getQuoteItemDealerCostTotal(): number {
    let total = 0;
    this.container.itemPrices?.forEach(x => {
      const supplierAdjustment = x.supplierPriceAdjustmentEnabled ? x.supplierPriceAdjustment : 0;
      total += money(x.supplierNettQuantityCost + supplierAdjustment, 2);
    });
    return total;
  }

  getQuoteItemMarginOrMarkupPercent(id: string): number {
    const price = this.findItemPrice(id);
    if (price == undefined) return 0;

    return price.percentMarginOrMarkup ?? this.quotePrice.percentMarginOrMarkup;
  }

  getQuoteItemDealerPriceAdjustment(id: string, breakDown?: QuoteItemPriceBreakdown): number {
    // Always lookup the price object for the item (active or deleted)
    const price = this.findItemPrice(id);
    if (!price) return 0;

    if (breakDown) {
      // Calculate proportional priceAdjustment for this breakdown
      const totalGross = price.supplierGrossQuantityCost;
      const breakdownGross = breakDown.supplierGrossQuantityCost ?? 0;
      const proportion = totalGross > 0 ? breakdownGross / totalGross : 0;
      const proportionalAdjustment = price.priceAdjustment * proportion;
      return proportionalAdjustment;
    }

    return price.priceAdjustment;
  }

  quoteItemTax(id: string, breakDown?: QuoteItemPriceBreakdown): number {
    const price = this.findItemPrice(id);
    if (price === undefined) return 0;

    const gross = breakDown?.calculatedGrossSellingPrice ?? price.calculatedGrossSellingPrice;
    return price.isTaxableItem ? gross * this.taxRate : 0;
  }

  quoteItemPrice(id: string, removeBuyInCost = false): number {
    const price = this.findItemPrice(id);
    if (price == undefined) return 0;

    let linePrice = price.calculatedGrossSellingPrice;

    if (removeBuyInCost) {
      const quoteItemBuyInData = this.container.itemsBuyInData?.find(x => x.id == id);

      if (quoteItemBuyInData != undefined) {
        const buyIns = fromJsonStr<StockLookupViewExtra[]>(quoteItemBuyInData.buyInData);

        const sum =
          buyIns?.reduce((accumulator, currentObject) => {
            return accumulator + (currentObject.calculatedGross ?? 0);
          }, 0) ?? 0;

        linePrice -= sum;
      }
    }

    return linePrice;
  }

  public needsQuote = sharedExecute(this._needsQuote.bind(this));
  /**
   * this will ensure at an async level that the quote propery is valid, before accessing the property synchronously
   * @returns true if the quote property is now valid
   */
  protected async _needsQuote(): Promise<boolean> {
    //override
    return Promise.resolve(false);
  }

  async getFreshQuoteCopy(): NullPromise<ResultGetQuote> {
    return await this.api.getQuote({ quoteId: this.quoteId });
  }

  public generateQuoteNumberOnSave() {
    this.numberSeed = this.quote.quoteOwnerId;
  }

  protected isSupplierReviewEnabled(): Promise<boolean> {
    return Promise.resolve(true);
  }

  async quoteStateChange(state: QuoteState): Promise<boolean> {
    const originalState = this.quoteState;
    const originalAllowSupplierApproval = this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval;
    const seed = this.numberSeed;
    const qn = this.quote.quoteNumber;

    const restoreQuoteState = () => {
      this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval = originalAllowSupplierApproval;
      this.quoteState = originalState;
      this.quote.quoteNumber = qn;
      this.numberSeed = seed;
      this.stateChangeReason = null;
    };
    const allowSupplierReview = await this.isSupplierReviewEnabled();
    const addSiblingStates = async (input: InputQuoteState[], rollback?: boolean) => {
      if (
        (state === QuoteState.Accepted && !this.quote.supplierApproved) ||
        (state === QuoteState.Approved && !allowSupplierReview)
      ) {
        //if we are here, we need to locate the rejected reason, and then reject any quotes
        //that are active or issued at the same time as accepting this quote
        const inputRejection = this.getQuoteSuperceededResult();
        this.siblings
          // eslint-disable-next-line no-bitwise
          ?.filter(x => flagInSet(x.state, QuoteState.Active | QuoteState.IssuePending | QuoteState.Issued))
          .forEach(s => {
            input.push({
              quoteId: s.quoteId,
              state: rollback ? s.state : QuoteState.Rejected,
              stateChangeReason: rollback ? null : inputRejection
            });
          });
      }
    };

    const performSave = async (rollback?: boolean) => {
      if (
        flagInSet(
          originalState,
          // eslint-disable-next-line no-bitwise
          QuoteState.Draft |
            QuoteState.Active |
            QuoteState.IssuePending |
            QuoteState.SupplierReviewPending |
            QuoteState.SupplierReviewed
        ) ||
        (originalState === QuoteState.Issued && !this.quote.supplierApproved)
      ) {
        let siblingUpdate: InputQuoteState[] | null = [];
        if (
          (this.quoteState === QuoteState.Accepted && !this.quote.supplierApproved) ||
          (this.quoteState === QuoteState.Approved && !allowSupplierReview)
        ) {
          await addSiblingStates(siblingUpdate, rollback);
          if (siblingUpdate.length === 0) siblingUpdate = null;
          if (this.isSupplierQuote && allowSupplierReview && !(await this.performSupplierMayApproveQuoteProcess()))
            return false;
        }

        //we need to use saveQuote here because the quote may have other things changed and edited.
        return await this.saveQuote(rollback, siblingUpdate);
      } else {
        const input: InputUpdateQuoteSetState = {
          quoteState: [
            {
              quoteId: this.quote.id,
              state: this.quoteState,
              stateChangeReason: null
            }
          ]
        };
        await addSiblingStates(input.quoteState, rollback);
        return await this.saveQuoteSetState(input);
      }
    };

    if (!(await this.quoteStateBeforeChange(state, originalState))) return false;

    this.quoteState = state;
    try {
      if (!(await performSave())) {
        restoreQuoteState();
        return false;
      }

      if (!(await this.quoteStateAfterChange(state, originalState))) {
        restoreQuoteState();
        await performSave(true);
        return false;
      }
    } catch (e) {
      restoreQuoteState();
      await performSave(true);
      throw e;
    }
    return true;
  }

  async performSupplierMayApproveQuoteProcess(): Promise<boolean> {
    const supplierName = this.supplierName;
    const v6Settings = v6ConfigSupplierOptions(this.quote.supplierId);
    const poNumberRequired = v6Settings.isPurchaseOrderNumberMandatory;
    const forceIssueOrder = v6Settings.isPurchaseOrderIssuedImmediately;
    const dialog = new QuoteTechnicalReviewDialog(
      poNumberRequired,
      forceIssueOrder,
      supplierName,
      this.quote.customQuoteNumber ?? ''
    );
    await dialog.showModal();
    if (!dialog.ok) return false;

    this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval = dialog.allowSupplierToIssue;
    this.quote.customQuoteNumber = dialog.orderNumber;
    return true;
  }

  async saveQuoteSetState(input: InputUpdateQuoteSetState) {
    const result = await this.api.updateQuoteSetState(input);
    if (!result) return false;
    const thisState = result.items.find(x => x.quoteId === this.quote.id);
    if (!thisState) throw new DevelopmentError(LangQuoteContainer.updateQuoteStateMissingThisQuote()); // Localized
    this.quoteState === thisState.state;
    this.quote.supplierApproved = thisState.supplierApproved;
    this.quote.recordVersion = thisState.recordVersion;
    this.backup.quote = clone(this.quote);
    result.items.forEach(thisState => {
      const sib = this.siblings?.find(x => x.quoteId === thisState.quoteId);
      if (sib) {
        sib.state = thisState.state;
        sib.recordVersion = thisState.recordVersion;
        sib.supplierApproved = thisState.supplierApproved;
      }
    });
    this.backup.siblings = clone(this.siblings?.filter(x => x.quoteId !== this.quote.id) ?? null);
    await this.doAfterSave();
    return true;
  }

  getQuoteSuperceededResult(): InputUpdateQuoteStateChangeReason {
    const x: InputUpdateQuoteStateChangeReason = {
      comment: '',
      finalStateReason: QuoteFinalStateReason.Superseded
    };
    return x;
  }

  async internalSaveQuote(siblings?: InputQuoteState[] | null) {
    this.quote.serviceProviderData = null;
    return await this.api.updateQuote({
      siblingQuoteState: siblings ?? null,
      quote: this.quote,
      quotePrice: this.quotePrice,
      numberSeed: this.numberSeed,
      stateChangeReason: this.stateChangeReason,
      quotePresentation: this.container.quotePresentation,
      assets: this.quoteUpdateAssets,
      providerDataDifference: null
    });
  }

  async runPricingAdjustments(options: {
    method: DealerQuotePriceAdjustmentMethod;
    value: number;
    isTaxableAdjustment: boolean;
    percentMarginOrMarkup?: number;
    dryRun: boolean;
    quoteItems?: QuoteItem[];
    quoteItemPrices?: QuoteItemPrice[];
    quotePrice?: QuotePrice;
    adjustmentTitle?: string;
    adjustmentDescription?: string;
    adjustmentComment?: string;
  }): NullPromise<{ quotePrice: QuotePrice; itemPrices: QuoteItemPrice[]; items: QuoteItem[] }> {
    if (options.dryRun) {
      if (!options.quoteItems || !options.quotePrice || !options.quoteItemPrices)
        throw new DevelopmentError('dryRun is true, but quote data is not provided');
    } else {
      if (options.quoteItems || options.quotePrice || options.quoteItemPrices)
        throw new DevelopmentError('dryRun is false, but quote data is provided');
    }
    const quotePrice = options.quotePrice ?? this.quotePrice;
    let quoteItems = options.quoteItems ?? this.quoteItems;
    const quoteItemPrices = options.quoteItemPrices ?? this.quoteItemPrices;
    let adjustmentPriceItem = quoteItems.find(x => isDealerQuotePriceAdjustment(x));

    if (options.percentMarginOrMarkup !== undefined) quotePrice.percentMarginOrMarkup = options.percentMarginOrMarkup;

    const input: InputUpdateQuotePrice = {
      quotePrice: quotePrice,
      quoteItemPrices: quoteItemPrices,
      commitChanges: !options.dryRun,
      quoteItems: quoteItems,
      dealerQuotePriceAdjustment: {
        adjustmentMethod: options.method,
        adjustmentValue: options.value,
        isTaxable: options.isTaxableAdjustment,
        title: options.adjustmentTitle ?? null,
        description: options.adjustmentDescription ?? null,
        comment: options.adjustmentComment ?? null
      }
    };
    const result = await this.api.updateQuotePrice(input);
    if (options.dryRun) {
      if (result?.resultFullQuotePrice) {
        adjustmentPriceItem = result.updatedQuoteItems.length > 0 ? result.updatedQuoteItems[0] : undefined;
        quoteItems = adjustmentPriceItem
          ? [...(quoteItems?.filter(x => !isDealerQuotePriceAdjustment(x)) ?? []), adjustmentPriceItem]
          : quoteItems?.filter(x => !isDealerQuotePriceAdjustment(x));

        if (result.resultFullQuotePrice.itemPrices.some(x => !quoteItems.find(y => y.id === x.id))) {
          await information('Bad Match');
          console.log('sds');
        }
        return {
          quotePrice: result.resultFullQuotePrice.quotePrice,
          itemPrices: result.resultFullQuotePrice.itemPrices,
          items: quoteItems ?? []
        };
      }
      return null;
    } else if (result) {
      if (result.updatedQuoteItems.length === 0) {
        const adjustmentPriceItem = this.quoteItems.find(x => isDealerQuotePriceAdjustment(x));
        if (adjustmentPriceItem)
          this.internalDeleteQuoteItemReference(adjustmentPriceItem.id, QuoteItemRowAction.HardDelete);
      } else {
        const adjustmentPriceItem = result.updatedQuoteItems[0];
        const adjustmentPrice =
          result.resultFullQuotePrice?.itemPrices.find(x => x.id === adjustmentPriceItem.id) ?? null;

        await this.processResultItem({
          quoteItem: adjustmentPriceItem,
          quoteItemPrice: adjustmentPrice,
          quoteItemBuyInData: null,
          quoteItemProviderData: null
        });
        this.performItemSorting();
      }

      await this.resetQuote(
        result.quote!, //never null when the input was not null
        result.resultFullQuotePrice!.quotePrice,
        result.quotePresentation,
        result.siblings, //as above
        this.container.quoteProviderData //no change
      );

      this.updateItemPrices(result.resultFullQuotePrice!.itemPrices);
      this.updateItemBuyIns(result.resultFullQuotePrice!.itemBuyIns);
      await this.validateBuyInPrices();

      fireQuickSuccessToast(LangQuoteContainer.quoteSaved({ title: this.quoteTitle }));
      await this.doAfterSave();

      return {
        quotePrice: this.quotePrice,
        itemPrices: this.quoteItemPrices,
        items: this.quoteItems
      };
    } else return null;
  }

  /**
   * this is called to send the current quote and quote price objects to update the server.
   * on sucessful update, the internal quote and price objects will be replaced with the new copies
   * from the server
   * when updating prices we also refresh all pricing for all items at the same time that may have been
   * reliant on the pricing.
   * calling this may result in some quoteItemContainers holding outdated objects that need replacement
   */
  async saveQuote(silently?: boolean, siblings?: InputQuoteState[] | null): Promise<boolean> {
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return false;
    }
    const result = await this.internalSaveQuote(siblings);

    if (result) {
      //quote update doesn't alter presentation, so we keep what we have
      await this.resetQuote(
        result.quote!, //never null when the input was not null
        result.resultFullQuotePrice!.quotePrice,
        result.quotePresentation,
        result.siblings,
        this.container.quoteProviderData //as above
      );
      this.updateItemPrices(result.resultFullQuotePrice!.itemPrices);
      this.updateItemBuyIns(result.resultFullQuotePrice!.itemBuyIns);
      await this.validateBuyInPrices();

      if (!silently) fireQuickSuccessToast(LangQuoteContainer.quoteSaved({ title: this.quoteTitle }));
      await this.doAfterSave();
      ``;
      return true;
    }
    return false;
  }

  /**
   * this will find the backup of a quote item and related data, and replace the items with a clone
   * of the backup. this will make any quoteItemContainers to this id stale and need replacing
   *
   * @param id the quote item id to replace
   * @returns a reference to the fresh copy of the data
   */
  restoreQuoteItemFromBackup(id: string | undefined): QuoteItemContainer | null {
    if (!id) return null;
    const backup = this.quoteItemContainer(id, true);
    this.replaceQuoteItem(backup, false);
    this.removeContainerCache(id);
    return this.quoteItemContainer(id);
  }

  changedItem(id: string | undefined): boolean {
    function comparable(qic: QuoteItemContainer) {
      const { clientSideCache, ...backup } = qic;
      return backup;
    }

    if (!id) return false;
    const backup = comparable(this.quoteItemContainer(id, true));
    const current = comparable(this.quoteItemContainer(id, false));

    const changed = !compare(backup, current);
    if (changed && globalThis?.isDevelopmentMode) {
      const backupFrame = JSON.parse(backup.data?.providerData ?? '{}');
      const currentFrame = JSON.parse(current.data?.providerData ?? '{}');
      console.debug('Quote item changed');
      console.debug('Frame Changes');
      console.debug(compareAnalyze(backupFrame, currentFrame));
      console.debug('Price Changes');
      console.debug(compareAnalyze(backup.price, current.price));
      console.debug('Item Changes');
      console.debug(compareAnalyze(backup.item, current.item));

      console.debug(backupFrame, currentFrame);
    }
    return changed;
  }

  changedItemQuantity(id: string | undefined): boolean {
    if (!id) return false;
    const backup = this.quoteItemContainer(id, true);
    const current = this.quoteItemContainer(id, false);

    return backup.item.quantity !== current.item.quantity;
  }

  async performItemSorting() {
    if (!this.container.items) await this.needsQuoteItems();
    else this.container.items = await this.internalSortItemsAsList();
  }

  /**
   * Updates the sort order of items in the container's quote presentation.
   *
   * @param {string[]} sortedOrder - The new sorted order of items.
   * @returns {Promise<boolean>} - A promise that resolves to true if the sort order was updated successfully, and false otherwise.
   */
  async updateItemSortOrder(sortedOrder: string[]) {
    if (!this.container.quotePresentation) return false;

    if (this.container.quotePresentation.itemDisplayOrder.length !== sortedOrder.length) return false;

    const sorted1 = this.container.quotePresentation.itemDisplayOrder.slice().sort();
    const sorted2 = sortedOrder.slice().sort();

    for (let i = 0; i < sorted1.length; i++) {
      if (sorted1[i] !== sorted2[i]) {
        return false;
      }
    }

    this.container.quotePresentation.itemDisplayOrder = sortedOrder;
    const saved = await this.saveQuote();
    if (saved) await this.performItemSorting();
    return saved;
  }
  quoteAttributesRequireSSIRecalculation(): boolean {
    return false;
  }
  anyFrameOutOfDate(_errors: string[]): Promise<boolean> {
    return Promise.resolve(false);
  }

  updateQuoteTermsAndConditions(terms: string | null | undefined) {
    if (this.container.quote) this.container.quote.termsAndConditions = terms ?? null;
  }

  /**
   * this should be called any time before accessing the quote items list, which may yet be unpopulated.
   * calling this makes all references to data invalid so it should not be called while there are active
   * items being used or edited.
   * @param forceRefresh force a reload of the data even if not required.
   * @returns true if suceeded
   */

  public needsQuoteItems = sharedExecute(this._needsQuoteItems.bind(this));
  private async _needsQuoteItems(forceRefresh = false): Promise<boolean> {
    const hydrateMissingProviderData = async (result: ResultGetQuoteItemsSummary) => {
      await Promise.all(result.data.map(item => this.hydrateQuoteItemProviderData(item)));
    };

    if (!this.container.items || forceRefresh) {
      this.itemContainerCache = [];
      this.itemContainerCacheBak = [];
      const result = await this.api.getQuoteItemsSummary({
        quoteId: this.quoteId,
        includeAllStates: true
      });

      if (result) {
        await hydrateMissingProviderData(result);
        const sortedItems: QuoteItem[] = await this.internalSortItemsAsList(result.items);

        this.container.items = sortedItems.filter(x => !isDeletedQuoteItemState(x));
        this.container.deletedItems = sortedItems.filter(x => isDeletedQuoteItemState(x));
        // Partition associated data sets into active vs deleted to avoid scanning later.
        this.container.itemsData = result.data.filter(
          d => !isDeletedQuoteItemState(sortedItems.find(i => i.id === d.id)!)
        );
        this.container.deletedItemsData = result.data.filter(d =>
          isDeletedQuoteItemState(sortedItems.find(i => i.id === d.id)!)
        );
        this.container.itemsBuyInData = result.quoteItemBuyInData.filter(
          d => !isDeletedQuoteItemState(sortedItems.find(i => i.id === d.id)!)
        );
        this.container.deletedItemsBuyInData = result.quoteItemBuyInData.filter(d =>
          isDeletedQuoteItemState(sortedItems.find(i => i.id === d.id)!)
        );
        this.container.itemPrices = result.prices.filter(
          p => !isDeletedQuoteItemState(sortedItems.find(i => i.id === p.id)!)
        );
        this.container.deletedItemPrices = result.prices.filter(p =>
          isDeletedQuoteItemState(sortedItems.find(i => i.id === p.id)!)
        );

        await this.validateBuyInPrices();

        this.backup.items = this.clone(this.container.items);
        this.backup.deletedItems = this.clone(this.container.deletedItems);
        this.backup.itemsData = this.clone(this.container.itemsData);
        this.backup.deletedItemsData = this.clone(this.container.deletedItemsData);
        this.backup.itemsBuyInData = this.clone(this.container.itemsBuyInData);
        this.backup.deletedItemsBuyInData = this.clone(this.container.deletedItemsBuyInData);
        this.backup.itemPrices = this.clone(this.container.itemPrices);
        this.backup.deletedItemPrices = this.clone(this.container.deletedItemPrices);
      } else return false;
    }
    return true;
  }

  public async hydrateQuoteItemProviderData(item: QuoteItemProviderData | null): Promise<void> {
    if (!item) return;
    if (item.providerData && item.providerData !== '') return;
    const dataPath = item.assetVirtualPaths?.['data'];
    if (!dataPath?.path) return;

    try {
      const providerData = await fetchQuoteAsset({
        quoteId: this.quoteId,
        quoteItemId: item.id,
        assetPath: dataPath.path,
        publicUrl: dataPath.publicUrl
      });
      item.providerData = providerData as string;
    } catch (error) {
      console.warn(
        `Falling back to legacy fetch for provider data for quote item ${item?.id} from path ${dataPath?.path}`,
        error
      );
    }
  }

  prepareForUnDelete(quoteItemId: string) {
    const item = this.container.deletedItems?.find(x => x.id === quoteItemId);
    if (item) {
      //  item.state = QuoteItemState.Active;

      // Remove from deletedItems
      this.container.deletedItems = this.container.deletedItems?.filter(x => x.id !== quoteItemId) ?? null;
      // Add to items
      if (!this.container.items) this.container.items = [];
      this.container.items.push(item);
      // Move provider data
      if (this.container.deletedItemsData) {
        const idx = this.container.deletedItemsData.findIndex(x => x.id === quoteItemId);
        if (idx >= 0) {
          const data = this.container.deletedItemsData.splice(idx, 1)[0];
          this.container.itemsData ??= [];
          this.container.itemsData.push(data);
        }
      }
      // Move buy-in data
      if (this.container.deletedItemsBuyInData) {
        const idx = this.container.deletedItemsBuyInData.findIndex(x => x.id === quoteItemId);
        if (idx >= 0) {
          const bid = this.container.deletedItemsBuyInData.splice(idx, 1)[0];
          this.container.itemsBuyInData ??= [];
          this.container.itemsBuyInData.push(bid);
        }
      }
      // Move price data
      if (this.container.deletedItemPrices) {
        const idx = this.container.deletedItemPrices.findIndex(x => x.id === quoteItemId);
        if (idx >= 0) {
          const price = this.container.deletedItemPrices.splice(idx, 1)[0];
          this.container.itemPrices ??= [];
          this.container.itemPrices.push(price);
        }
      }
      return this.quoteItemContainer(quoteItemId);
    }
    return undefined;
  }
  saveQuoteCalculations(_v: QCQuoteValues | null | undefined) {
    //override this method to save the quote calculations
  }

  saveQuoteAnnotations(_v: V6QuoteAnnotation[] | null | undefined) {
    //override this method to save the quote annotations
  }

  quoteProviderData(): QuoteProviderData | null {
    return this.container.quoteProviderData;
  }

  quoteItemContainerCache(quoteItemId: string, useBackup = false): QuoteItemContainer {
    const cache = useBackup ? this.itemContainerCacheBak : this.itemContainerCache;
    let result = cache.find(x => x.item.id === quoteItemId);
    if (!result) {
      const container = useBackup ? this.backup : this.container;
      const item =
        container.items?.find(x => x.id === quoteItemId) ?? container.deletedItems?.find(x => x.id === quoteItemId);

      const data =
        container.itemsData?.find(x => x.id == quoteItemId) ??
        container.deletedItemsData?.find(x => x.id == quoteItemId);
      const buyInData =
        container.itemsBuyInData?.find(x => x.id == quoteItemId) ??
        container.deletedItemsBuyInData?.find(x => x.id == quoteItemId);
      const price =
        container.itemPrices?.find(x => x.id == quoteItemId) ??
        container.deletedItemPrices?.find(x => x.id == quoteItemId);
      if (!item || !price) throw new Error(LangQuoteContainer.invalidQuoteItemId()); // Localized
      result = {
        item: item,
        data: data ?? null,
        buyInData: buyInData?.buyInData ?? undefined,
        price: price
      };
      cache.push(result);
    }

    return result;
  }

  quoteItemContainer(quoteItemId: string, useBackup = false): QuoteItemContainer {
    return this.quoteItemContainerCache(quoteItemId, useBackup);
  }

  quoteItemContainerClone(quoteItemId: string, useBackup = false): QuoteItemContainer {
    return this.cloneItemContainer(this.quoteItemContainerCache(quoteItemId, useBackup));
  }

  /**
   * Create a new quote item and add it to this quote instance. will ensure the quote items list is valid first before adding
   *
   * @param input parameters for the new quote item
   * @returns a valid object on success
   */
  async createQuoteItem(
    input: ParamCreateQuoteItem,
    thumbnail?: string,
    thumbnailExt?: string
  ): NullPromise<QuoteItemContainer> {
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return null;
    }

    const newId = input.id ?? newGuid();
    //ensure we have a correct listing before adding more items
    await this.needsQuoteItems();
    const inputParam: InputCreateQuoteItem = {
      quoteId: this.quoteId,
      quoteItemId: newId,
      assets: thumbnail
        ? {
            thumbnail: {
              assetPath: this.api.createQuoteItemAssetPath('thumbnail', thumbnailExt ?? '.svg'),
              data: thumbnail,
              delete: false,
              dataBase64: null,
              hasContent: false
            }
          }
        : {},

      ...input
    };
    const result = await this.api.createQuoteItem(inputParam);
    if (result) {
      await this.addQuoteItem(
        result.quoteItem,
        result.quoteItemPrice,
        result.quoteItemProviderData,
        result.quoteItemBuyInData
      );

      await this.resetQuote(
        result.quote,
        result.resultFullQuotePrice.quotePrice,
        result.quotePresentation,
        null,
        this.container.quoteProviderData
      );
      if (result.resultFullQuotePrice.itemPrices) this.updateItemPrices(result.resultFullQuotePrice.itemPrices);
      if (result.resultFullQuotePrice.itemBuyIns) this.updateItemBuyIns(result.resultFullQuotePrice.itemBuyIns);

      await this.validateBuyInPricesForItem(result.quoteItem.id);
      await this.doAfterSave();
      return this.quoteItemContainer(result.quoteItem.id);
    }
    return null;
  }

  /**
   * update a quote item on the server and its thumbnail svg image
   * after update refreshes the quote and quote price object, and all item prices
   * @param quoteItemContainer
   * @param thumbnail
   * @returns the new refreshed copy for the quoteitem
   */
  async createQuoteItemEx(
    quoteItemContainer: QuoteItemContainer,
    assets: { [key: string]: AttachedBlobAsset },
    extraItems?: InputUpdateQuoteItems
  ): Promise<QuoteItemContainer> {
    this._lastSaveSuccessful = false;

    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return quoteItemContainer;
    }
    const updateItemInput: InputUpdateQuoteItem = this.quoteItemContainerAsUpdateItemInput(assets, quoteItemContainer);
    this._lastSaveSuccessful = await this.procesItemUpdateBatch(updateItemInput, QuoteItemRowAction.Insert, extraItems);
    return this.quoteItemContainer(quoteItemContainer.item.id);
  }

  private quoteItemContainerAsUpdateItemInput(
    assets: { [key: string]: AttachedBlobAsset },
    quoteItemContainer: QuoteItemContainer
  ): InputUpdateQuoteItem {
    return {
      assets,

      quoteItem: quoteItemContainer.item,
      providerDataDifference: quoteItemContainer.differenceReport
        ? (toJsonStr(quoteItemContainer.differenceReport) ?? null)
        : null,
      quoteItemBuyInData: { data: quoteItemContainer.buyInData ?? null },
      quoteItemPrice: quoteItemContainer.price
    };
  }

  public async copyQuoteItem(quoteItemContainer: QuoteItemContainer): NullPromise<QuoteItemContainer> {
    let value: QuoteItemContainer | null = null;
    try {
      if (!(await this.copyQuoteItemBefore(quoteItemContainer))) return null;

      value = await this.copyQuoteItemExecute(quoteItemContainer);
      if (!value) return null;
      if (!(await this.copyQuoteItemAfter(quoteItemContainer, value))) {
        try {
          if (value) await this.deleteQuoteItem(value);
        } catch (e) {
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          throw new DevelopmentError(`trying to delete quoteitem after failed copy. ${(e as object).toString()}`);
        }
        return null;
      }
      return value;
    } finally {
      await this.copyQuoteItemFinally(quoteItemContainer, value);
    }
  }

  async processResultItem(item: ResultQuoteItem) {
    await this.hydrateQuoteItemProviderData(item.quoteItemProviderData);
    if (!item.quoteItem) return;
    const newContainer: QuoteItemContainer = {
      item: item.quoteItem,
      data: item.quoteItemProviderData,
      price: item.quoteItemPrice!,
      buyInData: item.quoteItemBuyInData ?? undefined
    };
    this.removeContainerCache(newContainer.item.id);
    if (this.hasItemById(newContainer.item.id)) {
      this.updateItemContainer(newContainer, false);
      this.updateItemContainer(newContainer, true);
    } else {
      if (item.quoteItem && item.quoteItemPrice)
        await this.addQuoteItem(
          item.quoteItem,
          item.quoteItemPrice,
          item.quoteItemProviderData,
          item.quoteItemBuyInData
        );
    }
    await this.validateBuyInPricesForItem(item.quoteItem.id);
  }

  async internalUpdateQuoteItems(primaryItem: UpdateQuoteItem | null, extraItems: InputUpdateQuoteItems) {
    //we need to save the quote first now, because there is a chance that the Quote Calculations which are
    //returned as part of ssi, but stored on the quote block storage.
    //we only want to update quote without updating any other stuff like quote items , or prices as this
    //will all be done in the stuff we are about to update
    if (this.quoteChanged()) await this.internalSaveQuote();

    const inputItems = clone(extraItems);
    if (primaryItem) inputItems.items.push(primaryItem);
    const result = await this.api.updateQuoteItems(inputItems);
    //remove deleted items from local cache
    inputItems.items.forEach(element => {
      if (
        flagEquals(
          element.action,
          QuoteItemRowAction.Delete,
          QuoteItemRowAction.HardDelete,
          QuoteItemRowAction.Undelete
        ) &&
        element.input.quoteItem
      )
        this.internalDeleteQuoteItemReference(element.input.quoteItem.id, element.action);
    });
    return result;
  }

  async procesItemUpdateBatch(
    updateItemInput: InputUpdateQuoteItem | null,
    action: QuoteItemRowAction,
    extraItems?: InputUpdateQuoteItems
  ) {
    /*
    const updateItemInput: InputUpdateQuoteItem | null = quoteItemContainer
      ? {
          quoteItemId: quoteItemContainer.item.id,
          providerDataDifference: quoteItemContainer.differenceReport
            ? toJsonStr(quoteItemContainer.differenceReport) ?? null
            : null,
          quoteItem: quoteItemContainer.item,
          quoteItemProviderData: quoteItemContainer.data,
          quoteItemBuyInData: { data: quoteItemContainer.buyInData ?? null },
          quoteItemPrice: quoteItemContainer.price
        }
      : null;
*/
    const result = await this.internalUpdateQuoteItems(
      updateItemInput
        ? {
            action: action,
            input: updateItemInput
          }
        : null,
      extraItems ?? { items: [] }
    );
    const resultQuote = result?.quote;
    const resultFullQuotePrice = result?.resultFullQuotePrice ?? undefined;
    const resultItems = result?.items ?? [];
    const resultQuotePresentation = result?.quotePresentation ?? undefined;

    if (resultFullQuotePrice && resultQuote) {
      await this.afterItemUpdateProcessing(resultItems, resultFullQuotePrice, resultQuote, resultQuotePresentation);

      return true;
    }
    return false;
  }

  cloneItemContainer(qic: QuoteItemContainer): QuoteItemContainer {
    return {
      buyInData: clone(qic.buyInData),
      data: clone(qic.data),
      item: clone(qic.item),
      price: clone(qic.price)
    };
  }

  /**
   * update a quote item on the server and its thumbnail svg image
   * after update refreshes the quote and quote price object, and all item prices
   * @param quoteItemContainer
   * @param thumbnail
   * @returns the new refreshed copy for the quoteitem
   */
  async saveAndUpdateQuoteItem(
    quoteItemContainer: QuoteItemContainer,
    assets: { [key: string]: AttachedBlobAsset },
    extraItems?: InputUpdateQuoteItems,
    forceSave?: boolean
  ): Promise<QuoteItemContainer> {
    this._lastSaveSuccessful = false;

    if (!forceSave && this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return quoteItemContainer;
    }

    const updateItemInput: InputUpdateQuoteItem = this.quoteItemContainerAsUpdateItemInput(assets, quoteItemContainer);
    this._lastSaveSuccessful = await this.procesItemUpdateBatch(updateItemInput, QuoteItemRowAction.Update, extraItems);

    return this.quoteItemContainer(quoteItemContainer.item.id);
  }

  public async runAndSaveSSI(_itemFilter?: string[] | undefined, _extraItems?: QuoteItemContainer[]) {
    if (!isSupplierUsingSSI(this.quote.supplierId)) return;

    const informationDispatcher = new InformationDispatcher();
    const setInfo = async (s: string, header = LangQuoteContainer.preparingToUpdate()) => {
      await informationDispatcher.setInformation(`# ${header}
                + ${s}`);
    };
    await setInfo(LangQuoteContainer.gettingUpdatesFromSupplier());
    const saveModal = new SaveWorkflowModal(LangQuoteContainer.updatingSupplierQuoteItem(), informationDispatcher, 3);

    //we dont want to wait for the modal to finish.
    const modalState = await saveModal.show();
    await modalState.onShow;
    try {
      //process ssi with new information
      //build an ssi processor that will build the quote exluding the deleted item, so that we get data
      //based on its non-existence
      const ssiProcessor = this.ssiProcessor(_itemFilter, _extraItems);
      const ssiInputs = await ssiProcessor.processSSI();
      await setInfo(LangQuoteContainer.supplierProcessingComplete());
      if (ssiInputs && ssiInputs.items.length > 0) await this.saveAndUpdateQuoteItems(ssiInputs);
    } finally {
      saveModal?.done();
      await saveModal?.hideModal();
    }
  }

  async saveAndUpdateQuoteItems(extraItems: InputUpdateQuoteItems): Promise<QuoteItemContainer[]> {
    this._lastSaveSuccessful = false;

    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return [];
    }

    this._lastSaveSuccessful = await this.procesItemUpdateBatch(null, QuoteItemRowAction.Update, extraItems);

    const result: QuoteItemContainer[] = [];
    extraItems.items
      .filter(x => !flagEquals(x.action, QuoteItemRowAction.Delete, QuoteItemRowAction.HardDelete))
      .forEach(element => {
        if (element.input.quoteItem) result.push(this.quoteItemContainer(element.input.quoteItem?.id));
      });
    return result;
  }

  async afterItemUpdateProcessing(
    resultItems: ResultQuoteItem[],
    resultFullQuotePrice: ResultFullQuotePrice,
    resultQuote: Quote,
    resultQuotePresentation?: QuotePresentation
  ) {
    for (const resultItem of resultItems) await this.processResultItem(resultItem);

    //saving item doesn't change presentation layer, pass in null
    await this.resetQuote(
      resultQuote,
      resultFullQuotePrice.quotePrice,
      resultQuotePresentation ?? null,
      null,
      this.container.quoteProviderData
    );

    if (resultFullQuotePrice?.itemPrices) this.updateItemPrices(resultFullQuotePrice.itemPrices);
    if (resultFullQuotePrice.itemBuyIns) this.updateItemBuyIns(resultFullQuotePrice.itemBuyIns);

    await this.validateBuyInPrices();
    await this.doAfterSave();
  }

  public async deleteQuoteItem(
    quoteItemContainer: QuoteItemContainer,
    extraItems?: InputUpdateQuoteItems
  ): Promise<boolean> {
    this._lastSaveSuccessful = false;
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return false;
    }

    await this.needsQuoteItems();
    await this.doBeforeDeleteQuoteItem(quoteItemContainer);
    const updateItemInput: InputUpdateQuoteItem = this.quoteItemContainerAsUpdateItemInput({}, quoteItemContainer);
    this._lastSaveSuccessful = await this.procesItemUpdateBatch(updateItemInput, QuoteItemRowAction.Delete, extraItems);

    if (this._lastSaveSuccessful) await this.doAfterDeleteQuoteItem(quoteItemContainer);
    return this._lastSaveSuccessful;
  }
  public async undeleteQuoteItem(
    quoteItemContainer: QuoteItemContainer,
    extraItems?: InputUpdateQuoteItems
  ): Promise<QuoteItemContainer | null> {
    this._lastSaveSuccessful = false;
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return null;
    }

    await this.needsQuoteItems();
    const updateItemInput: InputUpdateQuoteItem = this.quoteItemContainerAsUpdateItemInput({}, quoteItemContainer);
    this._lastSaveSuccessful = await this.procesItemUpdateBatch(
      updateItemInput,
      QuoteItemRowAction.Undelete,
      extraItems
    );
    const result = this.quoteItemContainer(quoteItemContainer.item.id);
    if (this._lastSaveSuccessful) await this.doAfterUndeleteQuoteItem(result);
    return result;
  }
  protected async doBeforeDeleteQuoteItem(_quoteItemContainer: QuoteItemContainer) {
    // do nothing
  }

  removeContainerCache(id: string) {
    let idx = this.itemContainerCache.findIndex(x => x.item.id === id);
    if (idx >= 0) this.itemContainerCache.splice(idx, 1);
    const found = idx >= 0;
    idx = this.itemContainerCacheBak.findIndex(x => x.item.id === id);
    if (idx >= 0) this.itemContainerCacheBak.splice(idx, 1);
    return found;
  }

  async saveAndUpdateQuoteItemWithIndicator(
    quoteItemContainer: QuoteItemContainer,
    thumbnail: string
  ): Promise<QuoteItemContainer> {
    if (!this.changedItem(quoteItemContainer.item.id)) {
      this._lastSaveSuccessful = true;
      return quoteItemContainer;
    }

    let result: QuoteItemContainer = quoteItemContainer;
    this._lastSaveSuccessful = await saveWithIndicator(async () => {
      if (!quoteItemContainer) return false;
      try {
        const newContainer = await this.saveAndUpdateQuoteItem(
          quoteItemContainer,
          thumbnail === ''
            ? {
                thumbnail: {
                  assetPath: this.api.createQuoteItemAssetPath('thumbnail', '.svg'),
                  data: thumbnail,
                  delete: thumbnail === '',
                  hasContent: false,
                  dataBase64: null
                }
              }
            : {}
        );
        if (newContainer.item.recordVersion !== quoteItemContainer.item.recordVersion) {
          result = newContainer;
          return true;
        }
      } catch {
        return false;
      }
      return false;
    });
    return result;
  }

  async saveAndUpdateQuoteItemPrice(qic: QuoteItemContainer): Promise<QuoteItemContainer> {
    const quoteItemPrice = qic.price;
    if (this.isReadonly()) {
      await showDevelopmentError('Trying to save readonly  quote ');
      return this.quoteItemContainer(quoteItemPrice.id);
    }

    const updateBuyInItem = (buyInData: string, useBackup: boolean) => {
      const container = useBackup ? this.backup : this.container;
      const idxBuyInData = container.itemsBuyInData?.findIndex(x => x.id === quoteItemPrice.id) ?? -1;

      if (container.itemsBuyInData) {
        if (idxBuyInData > -1 && !buyInData) container.itemsBuyInData?.splice(idxBuyInData, 1);
        else if (idxBuyInData > -1 && buyInData) container.itemsBuyInData[idxBuyInData].buyInData = buyInData;
        else if (idxBuyInData < 0 && buyInData)
          container.itemsBuyInData.push({
            buyInData: buyInData,
            dateCreated: localDateTimeToServer(new Date()),
            id: quoteItemPrice.id,
            recordVersion: ''
          });
      }
    };

    const result = await this.api.updateQuoteItem({
      quoteItem: qic.item,
      assets: {},
      providerDataDifference: null,
      quoteItemBuyInData: null,
      quoteItemPrice: quoteItemPrice
    });
    if (result?.quoteItemPrice && result.resultFullQuotePrice) {
      if (result.quoteItemBuyInData) {
        const newBuyInData = result.quoteItemBuyInData;
        updateBuyInItem(newBuyInData, false);
        updateBuyInItem(newBuyInData, true);
      }

      await this.afterItemUpdateProcessing([result], result.resultFullQuotePrice, result.quote, undefined);

      const container = this.quoteItemContainer(quoteItemPrice.id);
      fireQuickSuccessToast(LangQuoteContainer.itemPriceUpdated({ title: container?.item.title }));
      return container;
    }

    return this.quoteItemContainer(quoteItemPrice.id);
  }

  /**
   * replaces all item price objects with a fresh set from the server.
   * @param prices a new set of item prices
   */
  updateItemPrices(prices: QuoteItemPrice[]) {
    const update = (list?: QuoteItemPrice[] | null) => {
      list?.forEach(itemPrice => {
        const newPrice = prices.find(x => x.id === itemPrice.id);
        if (newPrice) Object.assign(itemPrice, newPrice);
      });
    };
    update(this.container.itemPrices);
    update(this.backup.itemPrices);
  }

  updateItemBuyIns(buyIns: QuoteItemBuyInData[]) {
    const update = (list?: QuoteItemBuyInData[] | null) => {
      list?.forEach(itemBuyIn => {
        const newBuyIn = buyIns.find(x => x.id === itemBuyIn.id);
        if (newBuyIn) Object.assign(itemBuyIn, newBuyIn);
      });
    };
    update(this.container.itemsBuyInData);
    update(this.backup.itemsBuyInData);
  }

  quoteAddressChanged(): boolean {
    return !compare(this.backup.quote?.shippingAddress, this.container.quote?.shippingAddress);
  }
  private hasPendingQuoteAssets(): boolean {
    if (!this.quoteUpdateAssets) return false;
    return Object.keys(this.quoteUpdateAssets).some(_key => true);
  }

  private stripServiceProviderData(quote: Quote | null): Quote | null {
    if (!quote) return quote;
    return { ...quote, serviceProviderData: null };
  }
  updatePendingQuoteAssets(assets: { [key: string]: AttachedBlobAsset | null | undefined }): void {
    if (!assets) return;
    this.quoteUpdateAssets = this.quoteUpdateAssets ?? {};
    Object.entries(assets).forEach(([key, asset]) => {
      if (!asset) delete this.quoteUpdateAssets![key];
      else this.quoteUpdateAssets![key] = asset;
    });
    if (!this.hasPendingQuoteAssets()) this.quoteUpdateAssets = null;
  }
  quoteChanged(): boolean {
    return (
      !compare(this.stripServiceProviderData(this.backup.quote), this.stripServiceProviderData(this.container.quote)) ||
      !compare(this.backup.quoteProviderData, this.container.quoteProviderData) ||
      !compare(this.backup.quotePrice, this.container.quotePrice) ||
      !compare(this.backup.quotePresentation, this.container.quotePresentation) ||
      this.hasPendingQuoteAssets()
    );
  }

  changed(): boolean {
    return !compare(this.backup, this.container) || this.hasPendingQuoteAssets();
  }

  public async makeCopy(): NullPromise<QuoteContainer> {
    return null;
  }

  public async makeAlternative(_forSupplierReview = false): NullPromise<QuoteContainer> {
    return null;
  }

  public async deleteQuote(): Promise<boolean> {
    return false;
  }

  public async getBuyInCosts(_buyInItems?: QuoteFrameBuyInItem[]): Promise<StockLookupViewExtra[] | undefined> {
    return undefined;
  }

  public lookupBuyInData(
    quoteItemContainer: QuoteItemContainer,
    code: string,
    supplierCode: string,
    lookup: string
  ): StockLookupViewExtra | undefined {
    if (!quoteItemContainer.buyInData) return;

    const buyIns = fromJsonStr<StockLookupViewExtra[]>(quoteItemContainer.buyInData);

    if (!buyIns) return;

    // eslint-disable-next-line consistent-return
    return buyIns.find(x => x.code == code && x.supplierCode == supplierCode && x.lookup == lookup);
  }

  public itemIsNotSortable(item: QuoteItem) {
    return isShipping(item) || isSSI(item) || isDealerQuotePriceAdjustment(item);
  }

  protected async loadSupplierName() {
    if (isEmptyOrSpace(this._supplierName))
      this._supplierName = await getQuoteSupplierDisplayName(this.quote.supplierId);
  }

  protected getItemById(id: string): QuoteItem | undefined {
    const item = this.container?.items?.find(x => x.id === id);
    if (!item && !this.container.deletedItems?.find(x => x.id === id))
      throw new DevelopmentError(`${id} missing in list`);
    return item;
  }

  protected quoteStateBeforeChange(_state: QuoteState, _original: QuoteState): Promise<boolean> {
    return Promise.resolve(true);
  }

  protected quoteStateAfterChange(_state: QuoteState, _original: QuoteState): Promise<boolean> {
    return Promise.resolve(true);
  }

  protected copyQuoteItemBefore(_quoteItemContainer: QuoteItemContainer): Promise<boolean> {
    return Promise.resolve(true);
  }

  protected copyQuoteItemAfter(
    _quoteItemContainer: QuoteItemContainer,
    _newQuoteItemContainer: QuoteItemContainer
  ): Promise<boolean> {
    return Promise.resolve(true);
  }

  protected async copyQuoteItemFinally(
    _quoteItemContainer: QuoteItemContainer,
    _newQuoteItemContainer: QuoteItemContainer | null
  ): Promise<void> {
    //
  }

  protected async copyQuoteItemExecute(quoteItemContainer: QuoteItemContainer): NullPromise<QuoteItemContainer> {
    const result = await this.api.duplicateQuoteItem({
      quoteItemId: quoteItemContainer.item.id
    });
    if (result) {
      await this.addQuoteItem(
        result.quoteItem,
        result.quoteItemPrice,
        result.quoteItemProviderData,
        result.quoteItemBuyInData?.buyInData ?? null
      );
      await this.resetQuote(
        result.quote,
        result.resultFullQuotePrice.quotePrice,
        result.quotePresentation,
        null,
        this.container.quoteProviderData
      );
      await this.validateBuyInPrices();
      await this.doAfterSave();
      return this.quoteItemContainer(result.quoteItem.id);
    }
    return null;
  }

  protected async doAfterDeleteQuoteItem(_quoteItemContainer: QuoteItemContainer) {
    //DoNothing
  }
  protected async doAfterUndeleteQuoteItem(_quoteItemContainer: QuoteItemContainer) {
    //DoNothing
  }

  protected internalIsReadonly(): boolean {
    return false;
  }

  /**
   * replaces the backups and originals of the quote objects with this new set of objects to become the master
   * @param quote
   * @param quotePrice
   * @param quotePresentation
   */
  protected async resetQuote(
    quote: Quote,
    quotePrice: QuotePrice,
    quotePresentation: QuotePresentation | null,
    siblings: QuoteSetSibling[] | null,
    providerData: QuoteProviderData | null
  ) {
    if (this.container.quote) Object.assign(this.container.quote, quote);
    else this.container.quote = quote;

    this.container.quote.serviceProviderData = null; //we never want to keep service provider data in memory
    const tmpProviderData =
      providerData ?? (await createQuoteProviderData(quote.serviceProvider, quote.supplierId, quote.quoteOwnerId));
    this.container.quoteProviderData = await validateAndUpgradeQuoteProviderData(tmpProviderData);
    if (quotePresentation) this.container.quotePresentation = quotePresentation;
    else if (!this.container.quotePresentation) throw new DevelopmentError('Missing QuotePresentation'); //should only occur on updates

    if (this.container.quotePrice) Object.assign(this.container.quotePrice, quotePrice);
    else this.container.quotePrice = quotePrice;

    if (siblings) this.container.siblings = siblings;

    this.backup.quote = this.clone(quote);
    this.backup.quoteProviderData = this.clone(this.container.quoteProviderData);
    this.backup.quotePrice = this.clone(quotePrice);

    //we dont pass in siblings if we didn't update the quote directly
    if (siblings) this.backup.siblings = this.clone(siblings);

    if (quotePresentation) this.backup.quotePresentation = this.clone(quotePresentation);
  }

  /**
   * execute all bound events after any save operation to allow for re-rendering and refreshing of state
   */
  protected async doAfterSave(): Promise<void> {
    await runEventNotify(this.afterSave);
  }

  protected async validateBuyInPricesForItem(id: string) {
    if (this.isBuyInDataLocked) return;
    const buyInData = this.container.itemsBuyInData?.find(x => x.id == id);

    const validationIssueIndex = this.priceValidation?.findIndex(x => x.id == id) ?? -1;

    if (validationIssueIndex > -1) this.priceValidation?.splice(validationIssueIndex, 1);

    if (buyInData) {
      await this.reportBuyInValidation(buyInData);
    }
  }

  protected async validateBuyInPrices() {
    if (this.isBuyInDataLocked) return;
    this.priceValidation = null;
    this.container.itemsBuyInData?.map(async x => {
      await this.reportBuyInValidation(x);
    });
  }

  protected async addQuoteItem(
    quoteItem: QuoteItem,
    quoteItemPrice: QuoteItemPrice,
    quoteItemProviderData: QuoteItemProviderData | null,
    quoteItemBuyInData: string | null
  ) {
    await this.hydrateQuoteItemProviderData(quoteItemProviderData);
    if (
      this.container.items?.find(x => x.id === quoteItem.id) ??
      this.container.deletedItems?.find(x => x.id === quoteItem.id)
    ) {
      await showDevelopmentError(`Quote item ${quoteItem.id} already exists in the quote`);
      return;
    }

    const isDeleted = quoteItem.state === QuoteItemState.Deleted;
    if (isDeleted) {
      this.container.deletedItems ??= [];
      this.container.deletedItems.push(quoteItem);
    } else {
      this.container.items ??= [];
      this.container.items.push(quoteItem);
    }
    if (quoteItemProviderData) {
      if (isDeleted) {
        this.container.deletedItemsData ??= [];
        this.container.deletedItemsData.push(quoteItemProviderData);
      } else {
        this.container.itemsData ??= [];
        this.container.itemsData.push(quoteItemProviderData);
      }
    }
    if (quoteItemBuyInData) {
      const buyInEntry: QuoteItemBuyInData = {
        id: quoteItem.id,
        buyInData: quoteItemBuyInData,
        dateCreated: localDateTimeToServer(new Date()),
        recordVersion: ''
      };
      if (isDeleted) {
        this.container.deletedItemsBuyInData ??= [];
        this.container.deletedItemsBuyInData.push(buyInEntry);
      } else {
        this.container.itemsBuyInData ??= [];
        this.container.itemsBuyInData.push(buyInEntry);
      }
    }
    if (this.container.itemPrices?.find(x => x.id === quoteItemPrice.id)) {
      await showDevelopmentError(`Quote item Price ${quoteItemPrice.id} already exists in the quote`);
      return;
    }

    if (this.container.itemPrices && this.container.deletedItemPrices) {
      if (isDeleted) this.container.deletedItemPrices.push(quoteItemPrice);
      else this.container.itemPrices.push(quoteItemPrice);
    } else {
      // Initialize if needed
      if (isDeleted) {
        this.container.deletedItemPrices ??= [];
        this.container.deletedItemPrices.push(quoteItemPrice);
      } else {
        this.container.itemPrices ??= [];
        this.container.itemPrices.push(quoteItemPrice);
      }
    }

    if (isDeleted) {
      this.backup.deletedItems ??= [];
      this.backup.deletedItems.push(this.clone(quoteItem));
    } else {
      this.backup.items ??= [];
      this.backup.items.push(this.clone(quoteItem));
    }
    if (quoteItemProviderData) {
      if (isDeleted) {
        this.backup.deletedItemsData ??= [];
        this.backup.deletedItemsData.push(this.clone(quoteItemProviderData));
      } else {
        this.backup.itemsData ??= [];
        this.backup.itemsData.push(this.clone(quoteItemProviderData));
      }
    }
    if (quoteItemBuyInData) {
      const buyInBackup: QuoteItemBuyInData = {
        id: quoteItem.id,
        buyInData: quoteItemBuyInData,
        dateCreated: localDateTimeToServer(new Date()),
        recordVersion: ''
      };
      if (isDeleted) {
        this.backup.deletedItemsBuyInData ??= [];
        this.backup.deletedItemsBuyInData.push(buyInBackup);
      } else {
        this.backup.itemsBuyInData ??= [];
        this.backup.itemsBuyInData.push(buyInBackup);
      }
    }
    if (isDeleted) {
      this.backup.deletedItemPrices ??= [];
      this.backup.deletedItemPrices.push(this.clone(quoteItemPrice));
    } else {
      this.backup.itemPrices ??= [];
      this.backup.itemPrices.push(this.clone(quoteItemPrice));
    }

    const sortedItems = await this.internalSortItemsAsList();
    this.container.items = sortedItems;
    this.backup.items = clone(sortedItems);
  }

  /**
   * perform an insert of text based blob data such as svg on the server
   * @param oldItemPath an old name to delete when uploading
   * @param newItemPath a new name path to use for this insert
   * @param imageData text based data such as svg
   */
  protected async postImageBlob(oldItemPath: string, newItemPath: string, imageData: string | null) {
    if (!imageData) return;
    const base64Index = imageData.indexOf('base64,');
    const data = base64Index >= 0 ? imageData.substring(imageData.indexOf(',') + 1) : btoa(imageData);

    await this.blobApi.updateFileByVirtualPath({
      oldVirtualPath: oldItemPath,
      newVirtualPath: newItemPath,
      data: data
    });
  }

  private getAdjustedPresentationOrder(items: QuoteItem[]): string[] {
    const presentationItems: string[] = [];
    const itemsToMoveToEnd: QuoteItem[] = items.filter(item => this.itemIsNotSortable(item));
    const order = this.container.quotePresentation?.itemDisplayOrder ?? [];

    order.forEach(commonId => {
      const item = items.find(x => x.commonId === commonId);
      if (item && !this.itemIsNotSortable(item)) presentationItems.push(commonId);
    });
    items
      .filter(x => !this.itemIsNotSortable(x) && !presentationItems.includes(x.commonId))
      .forEach(item => {
        presentationItems.push(item.commonId);
      });

    itemsToMoveToEnd.forEach(item => {
      if (!isShipping(item) && isSSI(item)) presentationItems.push(item.commonId);
    });
    itemsToMoveToEnd.forEach(item => {
      if (isShipping(item)) presentationItems.push(item.commonId);
    });
    itemsToMoveToEnd.forEach(item => {
      if (isDealerQuotePriceAdjustment(item)) presentationItems.push(item.commonId);
    });
    return presentationItems;
  }

  private async internalSortItemsAsList(items?: QuoteItem[]) {
    const sortingItems = items ?? this.container.items ?? [];
    if (!this.container.quotePresentation) {
      await showDevelopmentError(LangQuoteContainer.quotePresentationMissing()); // Localized
      return sortingItems;
    }
    const sortedItems: QuoteItem[] = [];
    const displayOrder = this.getAdjustedPresentationOrder(sortingItems);
    this.container.quotePresentation.itemDisplayOrder = displayOrder;

    //create a list ordered by the quote presentation
    displayOrder.forEach(commonId => {
      const item = sortingItems.find(item => item.commonId === commonId);
      if (item) {
        sortedItems.push(item);
      }
    });
    if (sortedItems.length !== sortingItems.length) {
      sortedItems.push(...sortingItems.filter(item => !displayOrder.includes(item.commonId)));
    }
    return sortedItems;
  }

  private updateItemContainer(updatedItemContainer: QuoteItemContainer, useBackup: boolean) {
    const container = useBackup ? this.backup : this.container;
    const idxItem = container.items?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;
    const idxItemDeleted =
      idxItem > -1 ? -1 : (container.deletedItems?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1);

    let idxData = -1;
    if (updatedItemContainer.data)
      idxData = container.itemsData?.findIndex(x => x.id === updatedItemContainer.data?.id) ?? -1;

    let idxBuyInData = -1;
    if (updatedItemContainer.buyInData)
      idxBuyInData = container.itemsBuyInData?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;

    const idxPrice = container.itemPrices?.findIndex(x => x.id === updatedItemContainer.price.id) ?? -1;

    if (idxItem >= 0 && container.items)
      container.items[idxItem] = useBackup ? this.clone(updatedItemContainer.item) : updatedItemContainer.item;
    if (idxItemDeleted >= 0 && container.deletedItems)
      container.deletedItems[idxItemDeleted] = useBackup
        ? this.clone(updatedItemContainer.item)
        : updatedItemContainer.item;

    if (idxData >= 0 && container.itemsData && updatedItemContainer.data)
      container.itemsData[idxData] = useBackup ? this.clone(updatedItemContainer.data) : updatedItemContainer.data;
    if (idxPrice >= 0 && container.itemPrices)
      container.itemPrices[idxPrice] = useBackup ? this.clone(updatedItemContainer.price) : updatedItemContainer.price;

    if (container.itemsBuyInData) {
      if (idxBuyInData > -1 && !updatedItemContainer.buyInData) container.itemsBuyInData?.splice(idxBuyInData, 1);
      else if (idxBuyInData > -1 && updatedItemContainer.buyInData)
        container.itemsBuyInData[idxBuyInData].buyInData = updatedItemContainer.buyInData;
      else if (idxBuyInData < 0 && updatedItemContainer.buyInData)
        container.itemsBuyInData.push({
          buyInData: updatedItemContainer.buyInData,
          dateCreated: localDateTimeToServer(new Date()),
          id: updatedItemContainer.item.id,
          recordVersion: ''
        });
    }
  }

  private async reportBuyInValidation(itemBuyInData: QuoteItemBuyInData) {
    if (this.isBuyInDataLocked) return;

    const metaDataForItem = fromJsonStr<StockLookupViewExtra[]>(itemBuyInData.buyInData);

    if (metaDataForItem) {
      const buyins = await this.getBuyInCosts(
        metaDataForItem.map(y => {
          return {
            code: y.code,
            partCode: '',
            extraDetails: {
              LibCode: y.libCode,
              SuppCode: y.supplierCode,
              SupplierId: y.supplierId
            },
            quantity: 1,
            resourceType: 0,
            decription: ''
          };
        })
      );

      //Once V6 returns the libcode, we need to filter on that as well
      // eslint-disable-next-line array-callback-return
      metaDataForItem.map(y => {
        const serverVersion = buyins?.find(
          z => z.code == y.code && y.supplierCode == z.supplierCode && y.supplierId == z.supplierId
        );

        // Check for missing costs
        if (y.cost == null || !serverVersion) {
          this.addPriceValidation(itemBuyInData.id, y.code, 'Missing cost price');
        }

        //Check for catalogue updates updates
        if (y.version !== serverVersion?.version) {
          this.addPriceValidation(itemBuyInData.id, y.code, 'Buy-in catalogue has changed');
        }
      });
    }
  }

  private internalDeleteQuoteItemReference(quoteItemId: string, action: QuoteItemRowAction) {
    function remove(list: { id: string }[]) {
      const idx = list.findIndex(x => x.id === quoteItemId) ?? -1;
      if (idx >= 0) list.splice(idx, 1);
    }

    if (action === QuoteItemRowAction.HardDelete) {
      if (this.container.items) remove(this.container.items);
      if (this.container.itemPrices) remove(this.container.itemPrices);
      if (this.container.itemsData) remove(this.container.itemsData);
      if (this.container.itemsBuyInData) remove(this.container.itemsBuyInData);
      // Also remove from deleted partitions if already soft-deleted earlier
      if (this.container.deletedItems) remove(this.container.deletedItems);
      if (this.container.deletedItemPrices) remove(this.container.deletedItemPrices);
      if (this.container.deletedItemsData) remove(this.container.deletedItemsData);
      if (this.container.deletedItemsBuyInData) remove(this.container.deletedItemsBuyInData);

      if (this.backup.items) remove(this.backup.items);
      if (this.backup.itemPrices) remove(this.backup.itemPrices);
      if (this.backup.itemsData) remove(this.backup.itemsData);
      if (this.backup.itemsBuyInData) remove(this.backup.itemsBuyInData);
      if (this.backup.deletedItems) remove(this.backup.deletedItems);
      if (this.backup.deletedItemPrices) remove(this.backup.deletedItemPrices);
      if (this.backup.deletedItemsData) remove(this.backup.deletedItemsData);
      if (this.backup.deletedItemsBuyInData) remove(this.backup.deletedItemsBuyInData);
    } else if (action === QuoteItemRowAction.Delete) {
      const item = this.container.items?.find(x => x.id === quoteItemId);
      if (item) {
        // Remove from items
        this.container.items = this.container.items?.filter(x => x.id !== quoteItemId) ?? null;
        // Add to deletedItems
        if (!this.container.deletedItems) this.container.deletedItems = [];
        this.container.deletedItems.push(item);

        // Move provider data
        const data = this.container.itemsData?.find(x => x.id === quoteItemId);
        if (data) {
          this.container.itemsData = this.container.itemsData?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.container.deletedItemsData) this.container.deletedItemsData = [];
          this.container.deletedItemsData.push(data);
        }
        // Move buy-in data
        const buyIn = this.container.itemsBuyInData?.find(x => x.id === quoteItemId);
        if (buyIn) {
          this.container.itemsBuyInData = this.container.itemsBuyInData?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.container.deletedItemsBuyInData) this.container.deletedItemsBuyInData = [];
          this.container.deletedItemsBuyInData.push(buyIn);
        }
        // Move price
        const price = this.container.itemPrices?.find(x => x.id === quoteItemId);
        if (price) {
          this.container.itemPrices = this.container.itemPrices?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.container.deletedItemPrices) this.container.deletedItemPrices = [];
          this.container.deletedItemPrices.push(price);
        }

        const backupItem = this.backup.items?.find(x => x.id === quoteItemId);
        if (backupItem) {
          // Remove from backup items
          this.backup.items = this.backup.items?.filter(x => x.id !== quoteItemId) ?? null;
          // Add to backup deletedItems
          if (!this.backup.deletedItems) this.backup.deletedItems = [];
          this.backup.deletedItems.push(backupItem);
        }
        // Backup provider data
        const backupData = this.backup.itemsData?.find(x => x.id === quoteItemId);
        if (backupData) {
          this.backup.itemsData = this.backup.itemsData?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.backup.deletedItemsData) this.backup.deletedItemsData = [];
          this.backup.deletedItemsData.push(backupData);
        }
        // Backup buy-in data
        const backupBuyIn = this.backup.itemsBuyInData?.find(x => x.id === quoteItemId);
        if (backupBuyIn) {
          this.backup.itemsBuyInData = this.backup.itemsBuyInData?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.backup.deletedItemsBuyInData) this.backup.deletedItemsBuyInData = [];
          this.backup.deletedItemsBuyInData.push(backupBuyIn);
        }
        // Backup price
        const backupPrice = this.backup.itemPrices?.find(x => x.id === quoteItemId);
        if (backupPrice) {
          this.backup.itemPrices = this.backup.itemPrices?.filter(x => x.id !== quoteItemId) ?? null;
          if (!this.backup.deletedItemPrices) this.backup.deletedItemPrices = [];
          this.backup.deletedItemPrices.push(backupPrice);
        }
      }
    } else if (action === QuoteItemRowAction.Undelete) {
      // Use existing prepareForUnDelete logic which migrates item + provider/buy-in/price data
      this.prepareForUnDelete(quoteItemId);
    }

    this.removeContainerCache(quoteItemId);
  }

  /**
   * find and replace the data elements of a quoteItem with a new refreshed set of data
   * uses a clone of the data passed in
   * @param updatedItemContainer
   * @param useBackup
   */
  private replaceQuoteItem(updatedItemContainer: QuoteItemContainer, useBackup: boolean) {
    const container = useBackup ? this.backup : this.container;
    const idxItem = container.items?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;

    let idxData = -1;
    if (updatedItemContainer.data)
      idxData = container.itemsData?.findIndex(x => x.id === updatedItemContainer.data?.id) ?? -1;

    const idxPrice = container.itemPrices?.findIndex(x => x.id == updatedItemContainer.price.id) ?? -1;
    if (idxItem >= 0 && container.items) container.items[idxItem] = this.clone(updatedItemContainer.item);
    if (updatedItemContainer.data && idxData >= 0 && container.itemsData)
      container.itemsData[idxData] = this.clone(updatedItemContainer.data);

    let idxBuyInData = -1;
    if (updatedItemContainer.buyInData)
      idxBuyInData = container.itemsBuyInData?.findIndex(x => x.id === updatedItemContainer.item.id) ?? -1;

    if (container.itemsBuyInData) {
      if (idxBuyInData > -1 && !updatedItemContainer.buyInData) container.itemsBuyInData?.splice(idxBuyInData, 1);
      else if (idxBuyInData > -1 && updatedItemContainer.buyInData)
        container.itemsBuyInData[idxBuyInData].buyInData = updatedItemContainer.buyInData;
      else if (idxBuyInData < 0 && updatedItemContainer.buyInData)
        container.itemsBuyInData.push({
          buyInData: updatedItemContainer.buyInData,
          dateCreated: localDateTimeToServer(new Date()),
          id: updatedItemContainer.item.id,
          recordVersion: ''
        });
    }

    if (idxPrice >= 0 && container.itemPrices) container.itemPrices[idxPrice] = this.clone(updatedItemContainer.price);
  }

  private hasItemById(id: string) {
    return (
      this.container?.items?.find(x => x.id === id) ??
      this.container?.deletedItems?.find(x => x.id === id) !== undefined
    );
  }
}
export function isDeletedQuoteItemState(item: QuoteItem) {
  return item.state === QuoteItemState.Deleted;
}
